import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import Breakpoint from 'components/Common/Breakpoint'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferCapacityWithAgesSelection from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import zIndex from 'styles/tools/z-index'
import LayoutContainer from 'components/Common/LayoutContainer'
import Backdrop from 'components/utils/Backdrop'
import {
  TOUR_V2_MAX_POSSIBLE_OCCUPANCIES,
  TOUR_V2_PRIVATE_MAX_POSSIBLE_OCCUPANCIES,
  LE_TOUR_V2_MAX_POSSIBLE_OCCUPANCIES,
} from 'constants/tours'
import Modal from 'components/Luxkit/Modal/Modal'

const Container = styled.div`
  width: auto;
  z-index: ${zIndex.searchMenu};
`

const TravellersSelectorContainer = styled.div`
  margin-top: ${rem(16)};
`

interface Props {
  isOpen: boolean
  isPrivateTour: boolean
  offerType: Tours.V2OfferType
  isLeTour: boolean
  onApply?: () => void
}

function TourV2TravellersSelect(props: Props) {
  const {
    isOpen,
    isPrivateTour,
    offerType,
    onApply,
    isLeTour,
  } = props

  const maxCapacityPerRoom = useMemo(() => {
    if (isPrivateTour) return TOUR_V2_PRIVATE_MAX_POSSIBLE_OCCUPANCIES[0].total
    if (isLeTour) return LE_TOUR_V2_MAX_POSSIBLE_OCCUPANCIES[0].total
    return TOUR_V2_MAX_POSSIBLE_OCCUPANCIES[0].total
  }, [isPrivateTour, isLeTour])

  const disclaimerMessage = useMemo(() => {
    if (offerType === 'connection_tour') {
      return 'You can add a maximum of 8 people'
    }
    return `Maximum capacity per room is ${maxCapacityPerRoom} ${isLeTour ? 'travellers' : 'guests'}`
  }, [offerType, maxCapacityPerRoom, isLeTour])

  const dispatch = useOfferPageDispatch()

  const maxCapacitiesOccupancies = useMemo(() => {
    if (isPrivateTour) return TOUR_V2_PRIVATE_MAX_POSSIBLE_OCCUPANCIES
    if (isLeTour) return LE_TOUR_V2_MAX_POSSIBLE_OCCUPANCIES
    return TOUR_V2_MAX_POSSIBLE_OCCUPANCIES
  },
  [isPrivateTour, isLeTour])

  const applySelection = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    onApply?.()
  }, [onApply, dispatch])

  return (
    <Container>
      <Backdrop onClick={applySelection} visible={isOpen} />
      <Breakpoint min="sm">
        <OfferCapacityWithAgesSelect
          capacities={maxCapacitiesOccupancies}
          disclaimer={disclaimerMessage}
          occupantUnit={isLeTour ? 'traveller' : 'guest'}
          childrenAgeLabel="at time of departure"
          onApply={applySelection}
          dropdownPosition="bottom-start"
        />
      </Breakpoint>

      <Breakpoint max="xs">
        <LayoutContainer>
          <OfferCapacityWithAgesSelectBox occupantUnit="traveller" />
        </LayoutContainer>
        <Modal
          isOpen={isOpen}
          onClose={applySelection}
          title="Travellers"
          secondaryActionText="Clear"
          primaryActionText="Apply"
          onPrimaryActionClick={applySelection}
        >
          <TravellersSelectorContainer>
            <OfferCapacityWithAgesSelection
              capacities={maxCapacitiesOccupancies}
              disclaimer={disclaimerMessage}
              childrenAgeLabel="at time of departure"
            />
          </TravellersSelectorContainer>
        </Modal>
      </Breakpoint>
    </Container>
  )
}

export default withOfferPageState((state) => ({
  isOpen: state.activeFilter === 'capacity',
}))(TourV2TravellersSelect)
